<template>
    <div class="w-full max-w-6xl mx-auto p-4">
        <div class="bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-100">
            <div class="p-8">
                <!-- Input Section -->
                <div class="flex flex-col md:flex-row gap-4 mb-8">
                    <div class="relative flex-grow group">
                        <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-gray-400 group-focus-within:text-[#fd5f2e] transition-colors" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                            </svg>
                        </div>
                        <input
                            v-model="url" 
                            @keyup.enter="fetchDescription"
                            type="text" 
                            placeholder="Paste YouTube URL here for Description (e.g. https://www.youtube.com/watch?v=...)" 
                            class="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#fd5f2e] focus:border-[#fd5f2e] outline-none transition-all shadow-sm text-gray-700 bg-gray-50 placeholder-gray-400"
                        />
                    </div>
                    <button 
                        @click="fetchDescription" 
                        :disabled="loading || !url"
                        class="px-8 py-3 bg-gradient-to-r from-[#fd5f2e] via-[#e63662] to-[#a41faf] hover:opacity-90 text-white font-bold rounded-xl shadow-lg hover:shadow-[#fd5f2e]/20 transform hover:-translate-y-0.5 transition-all disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none flex items-center justify-center min-w-[160px]"
                    >
                        <span v-if="loading" class="flex items-center">
                            <svg class="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            Extracting...
                        </span>
                        <span v-else>Get Description</span>
                    </button>
                </div>

                <!-- Error Message -->
                <div v-if="error" class="mb-8 bg-red-50 border-l-4 border-red-500 p-4 rounded-r shadow-sm animate-fade-in-down">
                    <div class="flex">
                        <div class="flex-shrink-0">
                            <svg class="h-5 w-5 text-red-500" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" />
                            </svg>
                        </div>
                        <div class="ml-3">
                            <p class="text-sm text-red-700 font-medium">
                                {{ error }}
                            </p>
                        </div>
                    </div>
                </div>

                <!-- Results Section -->
                <div v-if="description" class="animate-fade-in-up">
                    <div class="mb-8 border-b border-gray-100 pb-6">
                        <h2 class="text-2xl font-bold text-gray-900 mb-2 leading-tight">
                            Description of {{ videoMetadata?.title }}
                        </h2>
                        <div class="flex flex-wrap items-center gap-4 text-sm text-gray-600">
                             <span class="flex items-center">
                                <span class="font-semibold mr-1">Author:</span> {{ videoMetadata?.author }}
                            </span>
                        </div>
                    </div>

                    <div class="grid grid-cols-1 lg:grid-cols-12 gap-8">
                        <!-- Left Column: Video Preview & Actions -->
                        <div class="lg:col-span-4 space-y-6">
                            <a 
                                v-if="videoId"
                                :href="`https://www.youtube.com/watch?v=${videoId}`" 
                                target="_blank"
                                class="relative block aspect-video rounded-xl overflow-hidden shadow-lg border border-gray-100 bg-black group"
                            >
                                <img 
                                    :src="videoMetadata?.thumbnail" 
                                    :alt="videoMetadata?.title"
                                    class="w-full h-full object-cover opacity-80 group-hover:opacity-100 transition-opacity"
                                >
                                <div class="absolute inset-0 flex items-center justify-center">
                                    <div class="w-16 h-16 bg-red-600 rounded-full flex items-center justify-center text-white shadow-2xl transform group-hover:scale-110 transition-all">
                                        <svg class="w-8 h-8 fill-current translate-x-0.5" viewBox="0 0 24 24">
                                            <path d="M8 5v14l11-7z"/>
                                        </svg>
                                    </div>
                                </div>
                            </a>
                            
                            <div class="space-y-3">
                                <button @click="copyToClipboard" 
                                    class="w-full flex items-center justify-center px-6 py-3 bg-gradient-to-r from-[#fd5f2e] to-[#e63662] text-white font-bold rounded-xl shadow-md hover:shadow-lg transition-all">
                                    <svg v-if="copied" xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
                                        <path d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9z" />
                                        <path fill-rule="evenodd" d="M4 5a2 2 0 012-2 3 3 0 003 3h2a3 3 0 003-3 2 2 0 012 2v11a2 2 0 01-2 2H6a2 2 0 01-2-2V5zm9.707 5.707a1 1 0 00-1.414-1.414L9 12.586l-1.293-1.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" />
                                    </svg>
                                    <svg v-else xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3" />
                                    </svg>
                                    {{ copied ? 'Copied' : 'Copy Description' }}
                                </button>

                                <button @click="downloadDescription" 
                                    class="w-full flex items-center justify-center px-6 py-3 bg-white border border-gray-200 text-gray-700 font-semibold rounded-xl shadow-sm hover:border-[#fd5f2e]/30 hover:text-[#fd5f2e] transition-all group">
                                    <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 mr-2 text-gray-400 group-hover:text-[#fd5f2e] transition-colors" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                                    </svg>
                                    Download Text
                                </button>
                            </div>
                        </div>

                        <!-- Right Column: Description Content -->
                        <div class="lg:col-span-8 flex flex-col">
                            <div class="flex items-center justify-between mb-4">
                                <h3 class="text-lg font-bold text-gray-900 flex items-center">
                                    <svg class="w-5 h-5 mr-2 text-[#fd5f2e]" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h7"/></svg>
                                    Extracted Description
                                </h3>
                            </div>
                            
                            <div class="flex-grow bg-[#FFFBF8] rounded-2xl border border-[#fd5f2e]/10 p-6 sm:p-8 max-h-[600px] overflow-y-auto custom-scrollbar shadow-inner">
                                <div class="prose prose-orange max-w-none text-gray-700 leading-relaxed whitespace-pre-wrap text-sm sm:text-base">
                                    {{ description }}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Premium Loader Section -->
                <div v-else-if="loading" class="py-20 flex flex-col items-center justify-center animate-fade-in">
                    <div class="relative">
                        <!-- Main Spinner Rings -->
                        <div class="w-24 h-24 border-4 border-gray-50 rounded-full"></div>
                        <div class="absolute top-0 left-0 w-24 h-24 border-4 border-t-[#fd5f2e] border-r-[#e63662] border-b-transparent border-l-transparent rounded-full animate-spin"></div>
                        <div class="absolute top-2 left-2 w-20 h-20 border-4 border-t-[#a41faf] border-r-transparent border-b-[#fd5f2e] border-l-transparent rounded-full animate-spin-slow"></div>
                        
                        <!-- Pulsing Glow -->
                        <div class="absolute inset-0 bg-gradient-to-tr from-[#fd5f2e]/20 to-[#a41faf]/20 rounded-full animate-pulse blur-xl"></div>
                        
                        <!-- Center Icon -->
                        <div class="absolute inset-0 flex items-center justify-center">
                            <svg class="w-10 h-10 text-[#fd5f2e] animate-bounce-slow" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                        </div>
                    </div>
                    
                    <div class="mt-8 flex items-center justify-center gap-3 px-4">
                        <h3 class="text-2xl font-bold bg-gradient-to-r from-[#fd5f2e] via-[#e63662] to-[#a41faf] bg-clip-text text-transparent">
                            Processing
                        </h3>
                        <div class="flex items-center gap-1.5 mt-2">
                            <span class="w-1.5 h-1.5 bg-[#fd5f2e] rounded-full animate-bounce [animation-delay:-0.3s]"></span>
                            <span class="w-1.5 h-1.5 bg-[#e63662] rounded-full animate-bounce [animation-delay:-0.15s]"></span>
                            <span class="w-1.5 h-1.5 bg-[#a41faf] rounded-full animate-bounce"></span>
                        </div>
                    </div>

                    <!-- Skeleton Content Preview -->
                    <div class="mt-12 w-full max-w-md space-y-4 px-6 opacity-40">
                        <div class="h-2.5 bg-gray-100 rounded-full w-full overflow-hidden relative">
                            <div class="absolute inset-0 bg-gradient-to-r from-transparent via-[#fd5f2e]/10 to-transparent w-full animate-shimmer"></div>
                        </div>
                        <div class="h-2.5 bg-gray-100 rounded-full w-5/6 mx-auto overflow-hidden relative">
                            <div class="absolute inset-0 bg-gradient-to-r from-transparent via-[#e63662]/10 to-transparent w-full animate-shimmer [animation-delay:0.2s]"></div>
                        </div>
                        <div class="h-2.5 bg-gray-100 rounded-full w-4/6 mx-auto overflow-hidden relative">
                            <div class="absolute inset-0 bg-gradient-to-r from-transparent via-[#a41faf]/10 to-transparent w-full animate-shimmer [animation-delay:0.4s]"></div>
                        </div>
                    </div>
                </div>

                <!-- Empty State -->
                <div v-else-if="!loading && !error" class="text-center py-12">
                    <div class="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gray-100 mb-4">
                        <svg class="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h7"></path>
                        </svg>
                    </div>
                    <h3 class="text-lg font-medium text-gray-900">No description extracted</h3>
                    <p class="text-gray-500 mt-1">Enter a YouTube URL above to extract its description.</p>
                </div>
            </div>
            
            <!-- Footer -->
            <div class="hidden md:flex bg-gray-50 px-8 py-4 border-t border-gray-100 justify-between items-center text-xs text-gray-500">
                <span>Supports most YouTube videos</span>
                <span>Powered by youtube-transcript-generator</span>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, computed } from 'vue';
import axios from 'axios';

const url = ref('');
const loading = ref(false);
const error = ref(null);
const description = ref(null);
const videoMetadata = ref(null);
const copied = ref(false);

const videoId = computed(() => {
    if (!url.value) return null;
    const pattern = /(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/i;
    const match = url.value.match(pattern);
    return match ? match[1] : null;
});

const fetchDescription = async () => {
    if (!url.value) return;

    loading.value = true;
    error.value = null;
    description.value = null;
    copied.value = false;

    try {
        const response = await axios.post('/get-description', {
            url: url.value
        });

        if (response.data.success) {
            description.value = response.data.description;
            videoMetadata.value = response.data.metadata;
        } else {
            error.value = response.data.error || 'Failed to fetch description.';
        }
    } catch (err) {
        if (err.response && err.response.data && err.response.data.error) {
            error.value = err.response.data.error;
        } else {
            error.value = 'An unexpected error occurred. Please check the URL and try again.';
        }
        console.error(err);
    } finally {
        loading.value = false;
    }
};

const copyToClipboard = () => {
    if (!description.value) return;
    
    navigator.clipboard.writeText(description.value).then(() => {
        copied.value = true;
        setTimeout(() => {
            copied.value = false;
        }, 2000);
    });
};

const downloadDescription = () => {
    if (!description.value) return;

    const element = document.createElement('a');
    const file = new Blob([description.value], { type: 'text/plain' });
    element.href = URL.createObjectURL(file);
    
    const filename = (videoMetadata.value?.title || 'description')
        .replace(/[^a-z0-9]/gi, '_')
        .toLowerCase() + '_description.txt';
        
    element.download = filename;
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
};
</script>

<style scoped>
.custom-scrollbar::-webkit-scrollbar {
    width: 6px;
}
.custom-scrollbar::-webkit-scrollbar-track {
    background: transparent;
}
.custom-scrollbar::-webkit-scrollbar-thumb {
    background: #e4e4e7;
    border-radius: 10px;
}
.custom-scrollbar::-webkit-scrollbar-thumb:hover {
    background: #d4d4d8;
}

@keyframes fadeInDown {
    from {
        opacity: 0;
        transform: translate3d(0, -10px, 0);
    }
    to {
        opacity: 1;
        transform: translate3d(0, 0, 0);
    }
}
.animate-fade-in-down {
    animation: fadeInDown 0.3s ease-out;
}

@keyframes fadeInUp {
    from {
        opacity: 0;
        transform: translate3d(0, 10px, 0);
    }
    to {
        opacity: 1;
        transform: translate3d(0, 0, 0);
    }
}
.animate-fade-in-up {
    animation: fadeInUp 0.4s ease-out;
}

/* New Premium Loader Animations */
@keyframes spin-slow {
    from { transform: rotate(0deg); }
    to { transform: rotate(-360deg); }
}
.animate-spin-slow {
    animation: spin-slow 3s linear infinite;
}

@keyframes bounce-slow {
    0%, 100% { transform: translateY(0); }
    50% { transform: translateY(-6px); }
}
.animate-bounce-slow {
    animation: bounce-slow 2s ease-in-out infinite;
}

@keyframes shimmer {
    0% { transform: translateX(-100%); }
    100% { transform: translateX(100%); }
}
.animate-shimmer {
    animation: shimmer 2s infinite;
}

@keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
}
.animate-fade-in {
    animation: fadeIn 0.5s ease-out;
}
</style>
